import React from "react";
import styled from "@emotion/styled";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import IconButton from "@mui/material/IconButton";
import { format_date } from "../../utils/helpers";
import { ReactComponent as ExternalIcon } from "../../assets/icons/external_link.svg";

const Container = styled("div")(({ theme }) => ({
  padding: "1rem 2rem",
}));

const SectionTitle = styled("div")(({ theme }) => ({
  fontSize: "14px",
  marginBottom: "1rem",
  color: theme.palette.text.secondary,
  [theme.breakpoints.down("xl")]: {
    fontSize: "18px",
  },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: "12px",
  backgroundColor: "#FFFFFF",

  height: "60vh",
  overflowY: "auto",
  overflowX: "hidden",
  marginTop:'16px',

  "& .MuiTable-root": {
    minWidth: 650,
    borderCollapse: "separate", // IMPORTANT for sticky header
  },
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  "& .MuiTableCell-root": {
    fontWeight: "600",
    fontSize: "16px",
    color: theme.palette.text.primary,
    borderBottom: `2px solid ${theme.palette.divider}`,
    padding: "16px",
    letterSpacing: "0.5px",
  },
}));

const StyledTableBody = styled(TableBody)(({ theme }) => ({
  "& .MuiTableRow-root": {
    "&:hover": {
      backgroundColor: "#F8FAFB",
    },

    borderBottom: `1px solid ${theme.palette.divider}`, // ✅ always
  },

  "& .MuiTableCell-root": {
    fontSize: "14px",
    color: theme.palette.text.primary,
    padding: "16px",
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const TitleCell = styled("div")(({ theme }) => ({
  fontWeight: "500",
  color: theme.palette.text.primary,
}));

const DescriptionCell = styled("div")(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: "14px",
  lineHeight: "1.5",
  fontWeight: "400",
}));

const DateCell = styled("div")(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: "14px",
  fontWeight: "400",
}));

const LinkButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.primary.secondary,
  padding: "8px",

  "&:hover": {
    backgroundColor: `${theme.palette.primary.main}15`,
  },

  "& .MuiSvgIcon-root": {
    fontSize: "20px",
  },
}));

const EmptyState = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "calc(60vh - 60px)", // Subtract header height
  color: theme.palette.text.secondary,
  fontSize: "14px",
  fontWeight: "400",
}));

const ReferencesTab = ({ references }) => {
  const handleLinkClick = (link) => {
    window.open(link, "_blank", "noopener,noreferrer");
  };

  return (
    <Container>

      <StyledTableContainer>
        <Table stickyHeader>
          <StyledTableHead>
            <TableRow>
              <TableCell width="20%">Title</TableCell>
              <TableCell width="10%" align="center">
                Link
              </TableCell>
              <TableCell width="50%">Description</TableCell>
              <TableCell width="20%" align="right">
                Last Updated
              </TableCell>
            </TableRow>
          </StyledTableHead>

          <StyledTableBody>
            {references && references.length > 0 ? (
              references.map((reference, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <TitleCell>{reference.title}</TitleCell>
                  </TableCell>

                  <TableCell align="center">
                    <LinkButton
                      onClick={() => handleLinkClick(reference.link)}
                      aria-label="open link"
                    >
                      <ExternalIcon />
                    </LinkButton>
                  </TableCell>

                  <TableCell>
                    <DescriptionCell>{reference.description}</DescriptionCell>
                  </TableCell>

                  <TableCell align="right">
                    <DateCell>{format_date(reference.updated_at)}</DateCell>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} sx={{ border: "none", padding: 0 }}>
                  <EmptyState>No records found</EmptyState>
                </TableCell>
              </TableRow>
            )}
          </StyledTableBody>
        </Table>
      </StyledTableContainer>
    </Container>
  );
};

export default ReferencesTab;
